import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../services/ApiService";
import { useAppContext } from "../../context/AppContext";
import Toast from "../../components/Toast";
import { getOrCreateDeviceId } from "../../utils/deviceId";

// Import the DSS logo
import dssLogo from "../../assets/images/dss-logo.png";

const DriverAccess: React.FC = () => {
  const {
    setPhotos,
    setToken,
    setDelivery,
    setPurchaseOrder,
    setLoads,
    setAttachments,
    setLoadItems,
    setMaterialfo,
    setSalesOrder,
    setProgressDeliveries,
    setMeterialSo,
  } = useAppContext();

  const navigate = useNavigate();
  const [soNumber, setSoNumber] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
    duration?: number;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!soNumber.trim()) {
      setToast({ message: "Please enter the Sales Order number", type: "error" });
      return;
    }

    if (!pin.trim()) {
      setToast({ message: "Please enter the PIN", type: "error" });
      return;
    }

    setLoading(true);

    try {
      const deviceId = getOrCreateDeviceId();
      const responseData = await ApiService.accessBySO({
        so_number: soNumber.trim(),
        pin: pin.trim(),
        device_id: deviceId,
      });

      if (responseData.success) {
        if (responseData?.cancelled) {
          setToast({ message: "This Order has been Cancelled.", type: "error" });
          setLoading(false);
          return;
        }

        setToast({
          message: "Access granted! Redirecting...",
          type: "success",
        });

        // Set all the context data
        setPurchaseOrder(responseData.data.purchase_order);
        setToken(responseData.api_token);
        setDelivery(responseData.data.delivery);
        setLoads(responseData.data.loads);
        setLoadItems(responseData.data.loadItems);
        setPhotos(responseData.data.photos || []);
        setAttachments(responseData.data.attachments || []);
        setSalesOrder(responseData.data.sales_order);
        setMeterialSo(responseData.data.meterial_so);
        setMaterialfo(responseData.data.material_fo);
        setProgressDeliveries(responseData.data.progress);

        // Store in localStorage
        localStorage.setItem("_token", responseData.api_token);
        localStorage.setItem("page", "index");
        localStorage.setItem("sales_order_id", responseData.data.sales_order.id);
        localStorage.setItem("zoho_sales_order_id", responseData.data.vendor.zoho_sales_order_id);
        localStorage.setItem("vendor_id", responseData.data.vendor.vendor_id);

        if (responseData.data.delivery) {
          localStorage.setItem("delivery_id", responseData.data.delivery.id || "");
          
          // Navigate based on delivery status
          const status = responseData.data.delivery.status;
          if (status === "open") {
            navigate("/");
          } else if (status === "assigned") {
            navigate("/pick-up");
          } else if (status === "picked_up") {
            navigate("/upload-weight-ticket");
          } else if (status === "reached") {
            navigate("/proof-of-dump-meterial");
          } else {
            navigate("/");
          }
        } else {
          navigate("/");
        }
      } else {
        // Make link expired errors permanent (duration: 0)
        const isExpiredError = responseData.message?.toLowerCase().includes('expired');
        setToast({ 
          message: responseData.message || "Access denied", 
          type: "error",
          duration: isExpiredError ? 0 : undefined
        });
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Error:", error);
      setToast({
        message: "Failed to connect. Please try again.",
        type: "error",
      });
      setLoading(false);
    }
  };

  return (
    <div className="dss-driver-access">
      {/* Background Pattern */}
      <div className="dss-bg-pattern"></div>

      <div className="dss-login-container">
        {/* Logo & Branding Section */}
        <div className="dss-brand-section">
          <div className="dss-logo-wrapper">
            <img src={dssLogo} alt="Decorative Stone Solutions" className="dss-logo-img" />
          </div>
          <h1 className="dss-company-name">Decorative Stone Solutions</h1>
          <div className="dss-company-address">
            197 Woodland Pkwy, STE 104 #813<br />
            San Marcos, CA 92069
          </div>
        </div>

        {/* Login Card */}
        <div className="dss-login-card">
          <div className="dss-card-header">
            <div className="dss-header-icon">
              <i className="bi bi-truck"></i>
            </div>
            <h2>Driver Delivery Portal</h2>
            <p>Enter your order details to start delivery</p>
          </div>

          <form onSubmit={handleSubmit} className="dss-login-form">
            <div className="dss-input-group">
              <label htmlFor="soNumber">
                <i className="bi bi-hash"></i>
                Sales Order Number
              </label>
              <div className="dss-input-wrapper">
                <input
                  type="text"
                  id="soNumber"
                  value={soNumber}
                  onChange={(e) => setSoNumber(e.target.value)}
                  placeholder="e.g., 11646"
                  autoComplete="off"
                />
                <span className="dss-input-highlight"></span>
              </div>
            </div>

            <div className="dss-input-group">
              <label htmlFor="pin">
                <i className="bi bi-key"></i>
                PIN
              </label>
              <div className="dss-input-wrapper">
                <input
                  type="password"
                  id="pin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter your PIN"
                  autoComplete="off"
                />
                <span className="dss-input-highlight"></span>
              </div>
            </div>

            <button
              type="submit"
              className="dss-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="dss-spinner"></span>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="bi bi-box-arrow-in-right"></i>
                  Start Delivery
                </>
              )}
            </button>
          </form>

          <div className="dss-card-footer">
            <div className="dss-divider">
              <span>Need Help?</span>
            </div>
            <div className="dss-contact-info">
              <p>
                <i className="bi bi-envelope"></i>
                <a href="mailto:billing@decorativestonesolutions.com">billing@decorativestonesolutions.com</a>
              </p>
              <p>
                <i className="bi bi-telephone"></i>
                <a href="tel:18006991878">1-800-699-1878</a>
              </p>
            </div>
          </div>
        </div>

        {/* Company Footer */}
        <div className="dss-company-footer">
          <p>© 2025 Decorative Stone Solutions</p>
          <p>Premium Landscape Materials & Delivery</p>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => setToast(null)}
        />
      )}

      <style>{`
        .dss-driver-access {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #1e3a5f 100%);
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .dss-bg-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.02) 0%, transparent 30%);
          pointer-events: none;
        }

        .dss-login-container {
          width: 100%;
          max-width: 420px;
          position: relative;
          z-index: 1;
        }

        .dss-brand-section {
          text-align: center;
          margin-bottom: 24px;
        }

        .dss-logo-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .dss-logo-img {
          max-width: 140px;
          max-height: 100px;
          object-fit: contain;
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
        }

        .dss-company-name {
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
          letter-spacing: 0.5px;
        }

        .dss-company-address {
          color: rgba(255, 255, 255, 0.8);
          font-size: 13px;
          line-height: 1.6;
        }

        .dss-login-card {
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 20px;
          padding: 32px;
          box-shadow: 
            0 20px 60px rgba(0, 0, 0, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .dss-card-header {
          text-align: center;
          margin-bottom: 28px;
        }

        .dss-header-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          box-shadow: 0 4px 15px rgba(30, 58, 95, 0.4);
        }

        .dss-header-icon i {
          font-size: 28px;
          color: #fff;
        }

        .dss-card-header h2 {
          color: #1e3a5f;
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 6px;
        }

        .dss-card-header p {
          color: #718096;
          font-size: 14px;
          margin: 0;
        }

        .dss-login-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .dss-input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .dss-input-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #4a5568;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .dss-input-group label i {
          color: #1e3a5f;
          font-size: 14px;
        }

        .dss-input-wrapper {
          position: relative;
        }

        .dss-input-wrapper input {
          width: 100%;
          padding: 16px 18px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 16px;
          color: #2d3748;
          background: #fff;
          transition: all 0.3s ease;
        }

        .dss-input-wrapper input:focus {
          outline: none;
          border-color: #1e3a5f;
          box-shadow: 0 0 0 4px rgba(30, 58, 95, 0.15);
        }

        .dss-input-wrapper input::placeholder {
          color: #a0aec0;
        }

        .dss-input-highlight {
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #2d5a87, #1e3a5f);
          transition: all 0.3s ease;
          transform: translateX(-50%);
        }

        .dss-input-wrapper input:focus + .dss-input-highlight {
          width: calc(100% - 4px);
        }

        .dss-submit-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(30, 58, 95, 0.4);
          margin-top: 8px;
        }

        .dss-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(30, 58, 95, 0.5);
          background: linear-gradient(135deg, #2d5a87 0%, #3d6a97 100%);
        }

        .dss-submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .dss-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .dss-submit-btn i {
          font-size: 18px;
        }

        .dss-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .dss-card-footer {
          margin-top: 24px;
          text-align: center;
        }

        .dss-divider {
          display: flex;
          align-items: center;
          margin-bottom: 16px;
        }

        .dss-divider::before,
        .dss-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        }

        .dss-divider span {
          padding: 0 16px;
          color: #a0aec0;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .dss-contact-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .dss-contact-info p {
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #718096;
          font-size: 13px;
        }

        .dss-contact-info i {
          color: #1e3a5f;
          font-size: 14px;
        }

        .dss-contact-info a {
          color: #1e3a5f;
          text-decoration: none;
          font-weight: 500;
        }

        .dss-contact-info a:hover {
          text-decoration: underline;
        }

        .dss-company-footer {
          text-align: center;
          margin-top: 24px;
        }

        .dss-company-footer p {
          margin: 0;
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
          line-height: 1.6;
        }

        .dss-company-footer p:first-child {
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
        }

        @media (max-width: 480px) {
          .dss-login-card {
            padding: 24px 20px;
            border-radius: 16px;
          }

          .dss-company-name {
            font-size: 20px;
          }

          .dss-logo-img {
            max-width: 120px;
          }
        }
      `}</style>
    </div>
  );
};

export default DriverAccess;
